// src/components/brand/EdgeLexMark.tsx
//
// The EdgeLex circuit-brain mark. This is the only component that should
// render the product mark; import it instead of reaching for a PNG directly.
//
// Which asset it renders depends on both the surface tone and the rendered
// size — small sizes take a contained form because the artwork is too dense to
// resolve. See ./edgeLexMarkAssets.ts for that rule and ./useSurfaceTone.ts for
// how the app theme is resolved.

import { markSourceFor, isContainedSize, type EdgeLexMarkSurface } from './edgeLexMarkAssets';
import { useSurfaceTone } from './useSurfaceTone';

export interface EdgeLexMarkProps {
  /** Rendered edge length in CSS pixels. */
  size?: number;
  /**
   * Surface the mark sits on. Leave as `auto` inside themed chrome; pass an
   * explicit value on surfaces that ignore the theme, such as the
   * always-dark authentication screens.
   */
  surface?: EdgeLexMarkSurface;
  /**
   * Accessible name. Defaults to empty, which marks the image decorative --
   * correct wherever the word "EdgeLex" or "Lex" already sits beside it.
   */
  alt?: string;
  className?: string;
  title?: string;
}

export function EdgeLexMark({
  size = 28,
  surface = 'auto',
  alt = '',
  className = '',
  title,
}: EdgeLexMarkProps) {
  const themeTone = useSurfaceTone();
  const tone = surface === 'auto' ? themeTone : surface;

  return (
    <img
      src={markSourceFor(size, tone)}
      alt={alt}
      title={title}
      width={size}
      height={size}
      decoding="async"
      draggable={false}
      data-edgelex-mark={tone}
      data-edgelex-mark-form={isContainedSize(size) ? 'contained' : 'open'}
      className={className}
      style={{ width: size, height: size }}
    />
  );
}

export default EdgeLexMark;
